import type { GroupMember } from '@app/shared';
import { graphTokenRequest, msalInstance } from './auth/msalConfig';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

const ROLE_GROUP_ENV: Record<string, string | undefined> = {
  admin: import.meta.env.VITE_GROUP_ID_ADMIN as string | undefined,
  loan_analyst: import.meta.env.VITE_GROUP_ID_LOAN_ANALYST as string | undefined,
  special_assets: import.meta.env.VITE_GROUP_ID_SPECIAL_ASSETS as string | undefined,
};

async function getGraphToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) throw new Error('not signed in');
  try {
    const r = await msalInstance.acquireTokenSilent({ ...graphTokenRequest, account });
    return r.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      const r = await msalInstance.acquireTokenPopup({ ...graphTokenRequest, account });
      return r.accessToken;
    }
    throw e;
  }
}

async function graphFetch<T>(path: string): Promise<T> {
  const token = await getGraphToken();
  const r = await fetch(`https://graph.microsoft.com/v1.0${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    throw new Error(`Graph ${r.status} ${r.statusText}${text ? `: ${text}` : ''}`);
  }
  return r.json() as Promise<T>;
}

interface GraphUser {
  id: string;
  displayName: string | null;
  mail: string | null;
  userPrincipalName: string | null;
}

interface GraphMembersResponse {
  value: GraphUser[];
  '@odata.nextLink'?: string;
}

// List the members of the security group that corresponds to the given app role.
// Returns user-shaped objects suitable for assignment dropdowns.
export async function listMembersForRole(role: 'admin' | 'loan_analyst' | 'special_assets'): Promise<GroupMember[]> {
  const groupId = ROLE_GROUP_ENV[role];
  if (!groupId) {
    console.warn(`[graph] No group id configured for role ${role}; set VITE_GROUP_ID_${role.toUpperCase()}`);
    return [];
  }
  const r = await graphFetch<GraphMembersResponse>(
    `/groups/${groupId}/members?$select=id,displayName,mail,userPrincipalName`,
  );
  return r.value
    .filter(u => !!u.id && !!u.displayName)
    .map(u => ({
      id: u.id,
      displayName: u.displayName ?? '',
      email: u.mail ?? u.userPrincipalName ?? null,
    }));
}
